import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MAT_LEGACY_SNACK_BAR_DEFAULT_OPTIONS as MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/legacy-snack-bar';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfigManagerModule, xpoConfigManagerProvider } from '@xpo-ltl/config-manager';
import { DataApiService, NotificationService } from '@xpo-ltl/data-api';
import { UserProfileComponent, xpoAuthConfigManagerConfiguration, XpoAuthenticationService } from '@xpo-ltl/ngx-auth';
import { XpoAccountPopoverModule } from '@xpo-ltl/ngx-ltl-core/account-popover';
import { XpoAppSwitcherPopoverModule } from '@xpo-ltl/ngx-ltl-core/app-switcher-popover';
import { XpoButtonModule } from '@xpo-ltl/ngx-ltl-core/button';
import { XpoCardModule } from '@xpo-ltl/ngx-ltl-core/card';
import { XpoConfirmDialogModule } from '@xpo-ltl/ngx-ltl-core/confirm-dialog';
import { XpoDialogModule } from '@xpo-ltl/ngx-ltl-core/dialog';
import { XpoPageNotFoundRoutingModule } from '@xpo-ltl/ngx-ltl-core/error-page';
import { XpoFeedbackModule } from '@xpo-ltl/ngx-ltl-core/feedback';
import { XpoFooterModule } from '@xpo-ltl/ngx-ltl-core/footer';
import { XpoIconModule } from '@xpo-ltl/ngx-ltl-core/icon';
import { XpoMessagingPopoverModule } from '@xpo-ltl/ngx-ltl-core/messaging-popover';
import { XpoShellModule } from '@xpo-ltl/ngx-ltl-core/shell';
import { XpoSnackBarModule } from '@xpo-ltl/ngx-ltl-core/snack-bar';
import { SdkHumanResourceModule } from '@xpo-ltl/sdk-humanresource';
import { PapaParseModule } from 'ngx-papaparse';
import { firstValueFrom, race, take, timer } from 'rxjs';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppHttpInterceptor } from './services/interceptor.service';
import { UserPermissionService } from './services/user-permission.service';
import { UserService } from './services/user.service';
import { YesNoPipe } from './shared/pipes/yes-no.pipe';
import { SharedModule } from './shared/shared.module';

export const appConfig = () => {
  return xpoConfigManagerProvider(
    xpoAuthConfigManagerConfiguration({
      firebaseOptions: environment.firebase,
    })
  );
};

/**
 * xpoAuthCanMatch()'s guards decide off XpoAuthenticationService.isLoggedIn$, which only
 * reflects reality once the auth library's own bootstrap (discovery + silent tryLogin) has
 * finished and called initLoadAuthState(true). Without this, the very first route's canMatch
 * can run against a stale/default isLoggedIn$ value, wrongly bouncing an already-authenticated
 * user's cold load (or a not-yet-authenticated deep link) into the wildcard route before
 * sign-in/restore can complete. Users may see a refresh session screen
 *
 * An APP_INITIALIZER runs (and is awaited) before Angular creates the root component, which is
 * also before the router's own initial-navigation listener fires - so blocking here delays the
 * very first canMatch evaluation until isLoggedIn$ is accurate, with no change needed to
 * app.component.ts or router config. The timer() fallback caps the wait: createUserManager()'s
 * own error path deliberately never calls initLoadAuthState() (to avoid throwing and blanking
 * the whole app - see its comments in @xpo-ltl/ngx-auth), so without a fallback here a broken or
 * slow auth init would block the app from ever rendering.
 */
const AUTH_INIT_TIMEOUT_MS = 8000;
export function waitForAuthInit(xpoAuthService: XpoAuthenticationService): () => Promise<unknown> {
  return () => firstValueFrom(race(xpoAuthService.initAuthLoad$.pipe(take(1)), timer(AUTH_INIT_TIMEOUT_MS)));
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    ConfigManagerModule,
    HttpClientModule,
    SharedModule,
    BrowserAnimationsModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    XpoAccountPopoverModule,
    XpoAppSwitcherPopoverModule,
    XpoMessagingPopoverModule,
    XpoButtonModule,
    XpoCardModule,
    XpoConfirmDialogModule,
    XpoDialogModule,
    XpoShellModule,
    XpoFeedbackModule,
    XpoSnackBarModule,
    SdkHumanResourceModule,
    PapaParseModule,
    MatMenuModule,
    MatDialogModule,
    XpoFooterModule,
    XpoIconModule,
    UserProfileComponent,
    AppRoutingModule, // Keep last but one
    XpoPageNotFoundRoutingModule, // Keep Last
  ],
  providers: [
    appConfig(),
    { provide: APP_INITIALIZER, useFactory: waitForAuthInit, deps: [XpoAuthenticationService], multi: true },
    DataApiService,
    NotificationService,
    DatePipe,
    DecimalPipe,
    UserService,
    UserPermissionService,
    YesNoPipe,
    { provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000, verticalPosition: 'top' } },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
